import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { PaymentsOrder } from "~/api/Api.ts";
import { apiClient } from "~/api/ApiClient";
import { ApiError } from "~/api/types";
import Button from "~/components/mantine/button/Button";
import { serviceTitle } from "~/components/services/orders/Orders";
import { useOrderPaymentCheck } from "~/components/services/orders/orderPaymentCheck";
import { AlertCard } from "~/components/ui/card/AlertCard";
import Modal from "~/components/ui/modal/Modal.tsx";
import PendingStatus from "~/components/ui/pendingStatus/PendingStatus";
import Spinner from "~/components/ui/spinner/Spinner.tsx";
import { stripePromise } from "~/helpers/load_stripe";
import { useStripeSession } from "~/hooks/useStripeSession";

import styles from "./Orders.module.scss";

type PendingOrderProps = {
  order: PaymentsOrder;
  refetchPendingOrders: () => void;
};

export const PendingOrder = ({
  order,
  refetchPendingOrders,
}: PendingOrderProps) => {
  const paymentCheck = useOrderPaymentCheck();

  const description =
    paymentCheck === "confirming"
      ? "Your payment is being confirmed. This usually takes a few seconds."
      : paymentCheck === "late"
        ? "Your payment is taking longer than usual to confirm. Refresh the page in a minute before paying or cancelling it."
        : "Please complete checkout or cancel it before placing another order.";

  const { mutate: cancelOrder } = useMutation({
    mutationFn: async () => {
      await apiClient.billing.ordersCancelPartialUpdate(order.id);
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.error?.error?.message || "Failed to cancel order";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
    onSuccess: () => {
      enqueueSnackbar("Order was successfully cancelled", {
        variant: "success",
      });

      refetchPendingOrders();
    },
  });

  const {
    clientSecret,
    isModalOpen,
    isCreatingSession,
    openSession,
    closeModal,
  } = useStripeSession({
    sessionType: "pay",
    onSuccess: () => {},
    onError: () => {},
  });

  const handleCloseModal = () => {
    closeModal();
    refetchPendingOrders();
  };

  return (
    <div className={styles.pendingOrderCard}>
      <AlertCard
        title="You have a pending order"
        type="neutral"
        content={
          <>
            <div>{description}</div>
            <div className={styles.productTitleWrapper}>
              <span className={styles.productTitle}>
                {serviceTitle[order.service_type]}
              </span>
              <span className={styles.amount}>
                {`$${(order.amount / 100).toFixed(2)}`}
              </span>
            </div>
          </>
        }
        actions={
          paymentCheck === "confirming" ? (
            <PendingStatus>Confirming payment…</PendingStatus>
          ) : (
            <>
              <Button
                variant="outlined"
                // @ts-expect-error TS2339 non-existing className
                className={styles.cancelBtn}
                onClick={() => cancelOrder()}
              >
                Cancel Order
              </Button>
              <Button onClick={openSession}>Proceed to Checkout</Button>
            </>
          )
        }
      />
      <Modal
        key="payPendingOrder"
        isOpen={isModalOpen}
        closeModal={handleCloseModal}
        formId="payPendingOrderForm"
        height="92%"
        width="80%"
        // @ts-expect-error TS2339 non-existing className
        modalLabel={<h3 className={styles.modalTitle}></h3>}
        forceWhiteBackground
      >
        {isCreatingSession && <Spinner />}
        {clientSecret && stripePromise && (
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        )}
      </Modal>
    </div>
  );
};
